import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import backgroundImage from './assets/Home/bg.png';

const Testimonial = () => {
  const sectionRef = useRef(null);
  const quoteRef = useRef(null);
  const separatorRef = useRef(null);
  const authorRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Split quote into words for animation
  const quoteText = '"Autometa Studio remains the smartest creative technology investment we have made, period."';
  const words = quoteText.split(' ');

  useEffect(() => {
    const sectionElement = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            // Create animation timeline
            const tl = gsap.timeline({ defaults: { ease: 'none' } });
            
            // Animate each word one by one (left to right typing effect)
            const wordElements = quoteRef.current?.querySelectorAll('.word');
            if (wordElements && wordElements.length > 0) {
              wordElements.forEach((word, index) => {
                // Set initial state - hidden with clip-path (reveal from left)
                gsap.set(word, { 
                  opacity: 0,
                  clipPath: 'polygon(0 0, 0 0, 0 100%, 0 100%)', // Hidden - no width
                });
                
                // Animate word appearing from left to right (typing effect)
                tl.to(
                  word,
                  {
                    opacity: 1,
                    clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)', // Fully visible
                    duration: 0.3,
                    ease: 'none', // Linear for typing effect
                  },
                  index * 0.12 // Stagger delay - like typing speed
                );
              });
            }
            
            // Animate separator line (width expand from left to right)
            tl.fromTo(
              separatorRef.current,
              {
                scaleX: 0,
                opacity: 0,
                transformOrigin: 'left center',
              },
              {
                scaleX: 1,
                opacity: 1,
                duration: 0.8,
                ease: 'power2.out',
              },
              '-=0.2'
            )
            // Animate author info (fade in + slide from right)
            .fromTo(
              authorRef.current,
              {
                opacity: 0,
                x: 30,
              },
              {
                opacity: 1,
                x: 0,
                duration: 0.8,
                ease: 'power2.out',
              },
              '-=0.5'
            );
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    if (sectionElement) {
      observer.observe(sectionElement);
    }

    return () => {
      if (sectionElement) {
        observer.unobserve(sectionElement);
      }
    };
  }, [hasAnimated]);

  return (
    <section 
      ref={sectionRef}
      className="bg-white py-16 md:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Light overlay for readability */}
      <div className="absolute inset-0 bg-white/90 z-0"></div>
      <div className="max-w-5xl mx-auto relative z-10">
        {/* Quote */}
        <div ref={quoteRef} className="mb-8 md:mb-12">
          <p className="text-3xl sm:text-4xl md:text-5xl lg:text-8xl font-extrabold text-ink text-center leading-tight">
            {words.map((word, index) => (
              <span
                key={index}
                className="word inline-block whitespace-nowrap"
                style={{ marginRight: word === 'period."' ? '0' : '0.25em' }}
              >
                {word}
              </span>
            ))}
          </p>
        </div>
        <div className='grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12 items-start'>
          {/* Separator Line */}
          <div 
            ref={separatorRef}
            className="w-full h-px bg-ink/20 mb-8 md:mb-12 lg:col-span-3 mt-8"
          ></div>

          {/* Author Info */}
          <div ref={authorRef} className="text-right lg:col-span-1">
            <p className="text-sm sm:text-base md:text-lg font-semibold text-ink mb-2">
              TIMM CHIUSANO
            </p>
            <p className="text-xs text-brand">
              VP of Production and Creative Services
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonial;

